import { supabase } from '../../core/config/supabase';
import { TournamentFollowEntity, TournamentFollowWithDetailsEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class TournamentFollowDataSource {
  async create(userId: string, tournamentId: string): Promise<TournamentFollowEntity> {
    const { data, error } = await supabase
      .from('tournament_follows')
      .insert({ user_id: userId, tournament_id: tournamentId })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as TournamentFollowEntity;
  }

  async delete(userId: string, tournamentId: string): Promise<void> {
    const { error } = await supabase
      .from('tournament_follows')
      .delete()
      .eq('user_id', userId)
      .eq('tournament_id', tournamentId);

    if (error) throw new Error(error.message);
  }

  async getFollowedTournaments(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<TournamentFollowWithDetailsEntity[]> {
    logger.debug('TournamentFollowDataSource', 'Calling RPC', { functionName: 'get_followed_tournaments' });

    const { data, error } = await supabase.rpc('get_followed_tournaments', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('TournamentFollowDataSource', 'getFollowedTournaments failed', { error });
      throw new Error(error.message);
    }

    logger.debug('TournamentFollowDataSource', 'Mapped items', { count: data?.length ?? 0 });

    return (data as TournamentFollowWithDetailsEntity[]) || [];
  }

  async isFollowing(userId: string, tournamentId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('tournament_follows')
      .select('id')
      .eq('user_id', userId)
      .eq('tournament_id', tournamentId)
      .maybeSingle();

    if (error) throw new Error(error.message);

    return data !== null;
  }

  async getFollowerCount(tournamentId: string): Promise<number> {
    const { data, error } = await supabase.rpc('get_tournament_follower_count', {
      target_tournament_id: tournamentId,
    });

    if (error) throw new Error(error.message);

    return data || 0;
  }
}
